import React, { useEffect, useRef, useState } from 'react'
import { jwtDecode } from 'jwt-decode'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState, useAppDispatch } from '../../redux/store'
import { updateProfileAction } from '../../redux/users/usersAction'
import { UpdateProfileRequest } from '../../types/userTypes'
import { selectToken, selectUsername } from '../../redux/keycloak/keycloakSelectors'
import { DetailButton } from '../../components/common/detail'
import {
  FormField,
  FormGrid,
  FormPageHead,
  FormSection,
  FormShell,
} from '../../components/common/form'

function profileFromToken(token: string | null, fallbackUsername: string) {
  if (!token) {
    return {
      firstName: '',
      lastName: '',
      username: fallbackUsername,
      email: '',
    }
  }
  try {
    const decoded = jwtDecode<{
      preferred_username?: string
      email?: string
      given_name?: string
      family_name?: string
      name?: string
    }>(token)
    const nameParts = (decoded.name || '').trim().split(/\s+/).filter(Boolean)
    return {
      firstName: decoded.given_name || nameParts[0] || '',
      lastName:
        decoded.family_name ||
        (nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''),
      username: decoded.preferred_username || fallbackUsername,
      email: decoded.email || '',
    }
  } catch {
    return {
      firstName: '',
      lastName: '',
      username: fallbackUsername,
      email: '',
    }
  }
}

function PasswordField({
  label,
  name,
  value,
  autoComplete,
  shown,
  span,
  onToggle,
  onChange,
  showLabel,
  hideLabel,
}: {
  label: string
  name: string
  value: string
  autoComplete: string
  shown: boolean
  span?: boolean
  onToggle: () => void
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  showLabel: string
  hideLabel: string
}) {
  return (
    <FormField label={label} span={span}>
      <div className="profile-pass">
        <input
          className="incyia-form-inp"
          name={name}
          type={shown ? 'text' : 'password'}
          autoComplete={autoComplete}
          value={value}
          onChange={onChange}
        />
        <button
          type="button"
          className="profile-pass-toggle"
          onClick={onToggle}
          aria-pressed={shown}
        >
          {shown ? hideLabel : showLabel}
        </button>
      </div>
    </FormField>
  )
}

function initialsOf(
  firstName?: string,
  lastName?: string,
  username?: string
) {
  const letters = `${(firstName || '').charAt(0)}${(lastName || '').charAt(0)}`.trim()
  return (letters || (username || '').slice(0, 2) || '—').toUpperCase()
}

export default function ProfilePage() {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const token = useSelector(selectToken)
  const username = useSelector(selectUsername)
  const roles = useSelector((state: RootState) => state.auth.roles)
  const [profileLoading, setProfileLoading] = useState(true)
  const [profileNotice, setProfileNotice] = useState('')
  const [reveal, setReveal] = useState({
    currentPassword: false,
    password: false,
    confirmPassword: false,
  })
  const profileLoaded = useRef(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    password: '',
    confirmPassword: '',
  })

  const [form, setForm] = useState<UpdateProfileRequest>({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
  })

  useEffect(() => {
    if (profileLoaded.current) return
    if (!token && !username) return
    profileLoaded.current = true
    const profile = profileFromToken(token, username || '')
    setForm((current) => ({
      ...current,
      ...profile,
      password: '',
    }))
    setProfileLoading(false)
  }, [token, username])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setProfileNotice('')
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async () => {
    const result = await dispatch(
      updateProfileAction({
        firstName: form.firstName,
        lastName: form.lastName,
        username: form.username,
        email: form.email,
      })
    )
    if (updateProfileAction.fulfilled.match(result)) {
      setProfileNotice(t('profilePage.saved'))
    }
  }

  const handlePasswordChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPasswordError('')
    setPasswordSuccess('')
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value,
    })
  }

  const handlePasswordSubmit = async () => {
    const currentPassword = passwordForm.currentPassword.trim()
    const password = passwordForm.password
    const confirmPassword = passwordForm.confirmPassword
    if (!currentPassword || !password || !confirmPassword) {
      setPasswordError(t('profilePassword.required'))
      setPasswordSuccess('')
      return
    }
    if (password !== confirmPassword) {
      setPasswordError(t('profilePassword.mismatch'))
      setPasswordSuccess('')
      return
    }
    setPasswordError('')
    const result = await dispatch(
      updateProfileAction({ password, currentPassword })
    )
    if (updateProfileAction.fulfilled.match(result)) {
      setPasswordSuccess(t('profilePassword.updated'))
      setPasswordForm({ currentPassword: '', password: '', confirmPassword: '' })
      return
    }
    const message = String(result.payload || '')
    if (/current password is incorrect/i.test(message)) {
      setPasswordError(t('profilePassword.currentInvalid'))
    } else if (/unable to verify/i.test(message)) {
      setPasswordError(t('profilePassword.verifyFailed'))
    } else {
      setPasswordError(message || t('profilePassword.verifyFailed'))
    }
  }

  const displayName =
    [form.firstName, form.lastName].filter(Boolean).join(' ') ||
    form.username ||
    t('profile')

  return (
    <div className="page-content">
      <FormPageHead
        eyebrow={t('profilePage.eyebrow')}
        title={t('profile')}
        subtitle={t('profilePage.subtitle')}
        backLabel={t('Back')}
      />

      <div className="profile-layout">
        <aside className="profile-card">
          <div className="profile-hero">
            <div className="profile-avatar" aria-hidden>
              {initialsOf(form.firstName, form.lastName, form.username)}
            </div>
            <strong>{displayName}</strong>
            <p className="profile-email">{form.email || '—'}</p>
            {roles?.length ? (
              <div className="profile-roles">
                {roles.map((role) => (
                  <span key={role} className="profile-role">
                    {t(`teamPage.roles.${role}`, { defaultValue: role })}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          <dl className="profile-meta">
            <div>
              <dt>{t('profilePage.username')}</dt>
              <dd>{form.username || '—'}</dd>
            </div>
            <div>
              <dt>{t('profilePage.email')}</dt>
              <dd>{form.email || '—'}</dd>
            </div>
          </dl>
        </aside>

        <div className="profile-forms">
          <FormShell>
            <FormSection
              title={t('profilePage.basicTitle')}
              description={t('profilePage.basicDesc')}
            >
              {profileLoading ? (
                <p className="profile-msg">{t('loading')}</p>
              ) : (
                <>
                  <FormGrid cols={2}>
                    <FormField label={t('profilePage.firstName')}>
                      <input
                        className="incyia-form-inp"
                        name="firstName"
                        value={form.firstName}
                        onChange={handleInputChange}
                        autoComplete="given-name"
                      />
                    </FormField>
                    <FormField label={t('profilePage.lastName')}>
                      <input
                        className="incyia-form-inp"
                        name="lastName"
                        value={form.lastName}
                        onChange={handleInputChange}
                        autoComplete="family-name"
                      />
                    </FormField>
                    <FormField label={t('profilePage.username')}>
                      <input
                        className="incyia-form-inp"
                        name="username"
                        value={form.username}
                        onChange={handleInputChange}
                        autoComplete="username"
                      />
                    </FormField>
                    <FormField label={t('profilePage.email')}>
                      <input
                        className="incyia-form-inp"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleInputChange}
                        autoComplete="email"
                      />
                    </FormField>
                  </FormGrid>
                  {profileNotice ? (
                    <p className="profile-msg profile-msg--ok" role="status">
                      {profileNotice}
                    </p>
                  ) : null}
                  <div className="profile-actions">
                    <DetailButton onClick={handleSubmit}>
                      {t('profilePage.update')}
                    </DetailButton>
                  </div>
                </>
              )}
            </FormSection>
          </FormShell>

          <FormShell>
            <FormSection
              title={t('profilePage.securityTitle')}
              description={t('profilePage.securityDesc')}
            >
              <FormGrid cols={2}>
                <PasswordField
                  label={t('profilePassword.current')}
                  name="currentPassword"
                  autoComplete="current-password"
                  value={passwordForm.currentPassword}
                  shown={reveal.currentPassword}
                  span
                  showLabel={t('profilePage.showPassword')}
                  hideLabel={t('profilePage.hidePassword')}
                  onToggle={() =>
                    setReveal((current) => ({
                      ...current,
                      currentPassword: !current.currentPassword,
                    }))
                  }
                  onChange={handlePasswordChange}
                />
                <PasswordField
                  label={t('profilePassword.next')}
                  name="password"
                  autoComplete="new-password"
                  value={passwordForm.password}
                  shown={reveal.password}
                  showLabel={t('profilePage.showPassword')}
                  hideLabel={t('profilePage.hidePassword')}
                  onToggle={() =>
                    setReveal((current) => ({
                      ...current,
                      password: !current.password,
                    }))
                  }
                  onChange={handlePasswordChange}
                />
                <PasswordField
                  label={t('profilePassword.confirm')}
                  name="confirmPassword"
                  autoComplete="new-password"
                  value={passwordForm.confirmPassword}
                  shown={reveal.confirmPassword}
                  showLabel={t('profilePage.showPassword')}
                  hideLabel={t('profilePage.hidePassword')}
                  onToggle={() =>
                    setReveal((current) => ({
                      ...current,
                      confirmPassword: !current.confirmPassword,
                    }))
                  }
                  onChange={handlePasswordChange}
                />
              </FormGrid>
              {passwordError ? (
                <p className="profile-msg profile-msg--error" role="alert">
                  {passwordError}
                </p>
              ) : null}
              {passwordSuccess ? (
                <p className="profile-msg profile-msg--ok" role="status">
                  {passwordSuccess}
                </p>
              ) : null}
              <div className="profile-actions">
                <DetailButton onClick={handlePasswordSubmit}>
                  {t('profilePassword.submit')}
                </DetailButton>
              </div>
            </FormSection>
          </FormShell>
        </div>
      </div>
    </div>
  )
}
